import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LicenseExpansionForm from "@/components/LicenseExpansionForm";
import LicenseSubmissionGate from "@/components/LicenseSubmissionGate";
import LicenseRequestHistory from "@/components/LicenseRequestHistory";
import {
  canManageCommercialLicenseRequests,
  hasActiveCommercialLicense,
  isSubmissionBlocked,
} from "@/lib/commercialLicenseRequests";
import {
  EXPANSION_KIND_CLIENT_LIMIT,
  EXPANSION_KIND_MODULE,
  EXPANSION_KIND_PLAN_UPGRADE,
  EXPANSION_KIND_RENEWAL,
  EXPANSION_KIND_USER_LIMIT,
  readCommercialPlanFromSystemInfo,
} from "@/lib/commercialLicenseExpansion";
import { allMainModulesActive, shouldOpenModuleExpansionPrefill } from "@/lib/hubModuleCommercialState";
import { useCommercialSubmissionGate } from "@/lib/useCommercialSubmissionGate";
import { PLATFORM_DESIGNER_BUYER_PREVIEW_BANNER } from "@/lib/platformDesignerBuyerPreview";
import { useAuth } from "@/lib/auth";

const QUICK_ACTIONS = [
  { id: "renewal", label: "Renovar licencia", kind: EXPANSION_KIND_RENEWAL },
  { id: "expansion", label: "Solicitar ampliación", kind: EXPANSION_KIND_MODULE },
  { id: "users", label: "Agregar usuarios", kind: EXPANSION_KIND_USER_LIMIT },
  { id: "clients", label: "Agregar clientes", kind: EXPANSION_KIND_CLIENT_LIMIT },
];

/**
 * Sección completa «Renovación y ampliación de licencia» — Mi Negocio / Licencia.
 */
export default function LicenseRenewalExpansionPanel({
  systemInfo,
  profileDefaults = null,
  previewMode = false,
  showHistory = false,
  prefill = null,
  onPrefillConsumed,
  forceVisible = false,
  modalFlow = false,
  onFormClose,
  /** Sin marco/título propio (p. ej. dentro de acordeón Mi Negocio). */
  embedded = false,
}) {
  const { user } = useAuth();
  const canManage = canManageCommercialLicenseRequests(user) && !previewMode;
  const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
  const licenseExpired =
    systemInfo?.subscription?.ok === false
    && (systemInfo?.subscription?.reason === "trial_expired"
      || systemInfo?.license?.status === "expired"
      || systemInfo?.license?.status === "missing");
  const allModulesActive = allMainModulesActive(systemInfo);
  const planCtx = useMemo(() => readCommercialPlanFromSystemInfo(systemInfo), [systemInfo]);
  const { gate, setGate, refreshGate, loading: gateLoading } = useCommercialSubmissionGate(
    user,
    Boolean(systemInfo) && !previewMode,
  );
  const submissionBlocked = isSubmissionBlocked(gate);

  const [formOpen, setFormOpen] = useState(false);
  const [initialPrefill, setInitialPrefill] = useState(null);

  useEffect(() => {
    if (!prefill) return;
    // No abrir «Agregar módulos» si la licencia ya autoriza lo solicitado / todos los módulos.
    if (!shouldOpenModuleExpansionPrefill(systemInfo, prefill)) {
      onPrefillConsumed?.();
      onFormClose?.();
      return;
    }
    setInitialPrefill(prefill);
    setFormOpen(true);
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed, onFormClose, systemInfo]);

  const effectivePrefill = initialPrefill;

  const visibleActions = useMemo(() => {
    if (licenseExpired || !hasActiveLicense) {
      return [QUICK_ACTIONS[0]];
    }
    let actions = QUICK_ACTIONS;
    if (allModulesActive || planCtx.plan === "professional") {
      actions = actions.filter((action) => action.id !== "expansion");
    }
    if (planCtx.hasPlan && planCtx.plan === "basic") {
      actions = [
        ...actions,
        { id: "plan_upgrade", label: "Cambiar a Profesional", kind: EXPANSION_KIND_PLAN_UPGRADE },
      ];
    }
    return actions;
  }, [allModulesActive, hasActiveLicense, licenseExpired, planCtx]);

  const openForm = (kind, moduleId = null) => {
    if (previewMode) {
      const requested_modules = {};
      if (kind === EXPANSION_KIND_MODULE && moduleId) {
        requested_modules[moduleId] = true;
      }
      setInitialPrefill({
        expansion_type: kind,
        requested_modules,
      });
      setFormOpen(true);
      return;
    }
    const requested_modules = {};
    if (kind === EXPANSION_KIND_MODULE && moduleId) {
      requested_modules[moduleId] = true;
    }
    setInitialPrefill({
      expansion_type: kind,
      requested_modules,
    });
    setFormOpen(true);
  };

  const handleGateChange = (nextGate) => {
    setGate(nextGate);
    refreshGate();
  };

  const closeForm = () => {
    setFormOpen(false);
    setInitialPrefill(null);
    onFormClose?.();
  };

  const formBody = previewMode ? (
    <div className="space-y-3 rounded-md border border-sky-200 bg-sky-50/50 p-4">
      <p className="text-xs text-sky-900 m-0 font-medium">
        Vista simulada — previsualización del formulario (no se enviará a BNT).
      </p>
      <LicenseExpansionForm
        systemInfo={systemInfo}
        profileDefaults={profileDefaults}
        gate={null}
        onGateChange={() => {}}
        onSubmitted={() => {}}
        user={user}
        initialExpansionType={effectivePrefill?.expansion_type}
        preselectedModules={effectivePrefill?.requested_modules}
        previewReadOnly
      />
    </div>
  ) : (
    <LicenseExpansionForm
      systemInfo={systemInfo}
      profileDefaults={profileDefaults}
      gate={gate}
      onGateChange={handleGateChange}
      onSubmitted={() => {
        refreshGate();
        closeForm();
      }}
      user={user}
      initialExpansionType={effectivePrefill?.expansion_type}
      preselectedModules={effectivePrefill?.requested_modules}
    />
  );

  if (!systemInfo) return null;

  const showSection = hasActiveLicense || licenseExpired || previewMode || forceVisible;
  if (!showSection) return null;

  return (
    <section
      className={
        embedded
          ? "space-y-4"
          : "rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4"
      }
      data-testid="license-renewal-expansion-panel"
    >
      {embedded ? null : (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2D5C] m-0">
            Licencia y ampliaciones
          </h2>
          <p className="text-xs text-slate-600 mt-1 mb-0 leading-relaxed">
            {planCtx.hasPlan
              ? "Renueva tu plan, ajusta usuarios/clientes o solicita el upgrade a Profesional. BNT revisará la solicitud en el Centro SaaS."
              : "Puedes solicitar renovación de vigencia o aumento de usuarios/clientes. BNT revisará la solicitud desde el Centro SaaS."}
          </p>
        </div>
      )}
      {embedded ? (
        <p className="text-xs text-slate-600 m-0 leading-relaxed">
          {planCtx.hasPlan
            ? "Renueva tu plan, ajusta usuarios/clientes o solicita el upgrade a Profesional. BNT revisará la solicitud en el Centro SaaS."
            : "Puedes solicitar renovación de vigencia o aumento de usuarios/clientes. BNT revisará la solicitud desde el Centro SaaS."}
        </p>
      ) : null}

      {previewMode ? (
        <p
          className="text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-2.5 py-1.5 m-0 leading-snug"
          data-testid="license-renewal-preview-notice"
        >
          Vista simulada — no se envían solicitudes reales. {PLATFORM_DESIGNER_BUYER_PREVIEW_BANNER}
        </p>
      ) : null}

      {gateLoading && canManage ? (
        <p className="text-xs text-slate-500 m-0 inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Verificando solicitudes pendientes…
        </p>
      ) : null}

      {canManage && submissionBlocked ? (
        <LicenseSubmissionGate gate={gate} onGateChange={handleGateChange} testId="license-renewal-submission-gate" />
      ) : null}

      {canManage && submissionBlocked ? (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 m-0">
          Ya tienes una solicitud pendiente en revisión. Espere respuesta de BNT antes de enviar otra.
        </p>
      ) : null}

      {allModulesActive && hasActiveLicense ? (
        <p
          className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 m-0"
          data-testid="license-all-modules-active-notice"
        >
          {planCtx.hasPlan
            ? `Tu plan ${planCtx.label} está activo e incluye todos los módulos del plan. Puedes renovar vigencia o aumentar usuarios/clientes.`
            : "Tu licencia está activa e incluye todos los módulos principales. Puedes solicitar renovación de vigencia o aumento de usuarios/clientes."}
        </p>
      ) : null}

      {!formOpen || modalFlow ? (
        <div className="flex flex-wrap gap-2" data-testid="license-renewal-quick-actions">
          {visibleActions.map((action) => (
            <Button
              key={action.id}
              type="button"
              variant={action.id === "renewal" && licenseExpired ? "default" : "outline"}
              size="sm"
              className={`h-9 text-xs ${action.id === "renewal" && licenseExpired ? "bg-[#0F2D5C] hover:bg-[#0a2247]" : ""}`}
              disabled={!previewMode && canManage && submissionBlocked}
              onClick={() => openForm(action.kind)}
              data-testid={`license-renewal-action-${action.id}`}
            >
              {action.label}
            </Button>
          ))}
          {hasActiveLicense && !allModulesActive ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 text-xs"
              disabled={!previewMode && canManage && submissionBlocked}
              onClick={() => openForm(EXPANSION_KIND_MODULE)}
              data-testid="license-renewal-action-module"
            >
              Solicitar módulo adicional
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900 m-0">Detalle de solicitud</h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={closeForm}
            >
              Cerrar
            </Button>
          </div>
          {formBody}
        </div>
      )}

      {modalFlow ? (
        <Dialog open={formOpen} onOpenChange={(open) => { if (!open) closeForm(); }}>
          <DialogContent
            className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm"
            data-testid="license-renewal-modal"
          >
            <DialogHeader>
              <DialogTitle className="text-base text-[#0F2D5C]">Solicitud comercial</DialogTitle>
            </DialogHeader>
            {formBody}
          </DialogContent>
        </Dialog>
      ) : null}

      {showHistory && canManage ? (
        <div className="pt-2 border-t border-slate-100">
          <h3 className="text-xs font-semibold text-slate-800 mb-2">Historial reciente</h3>
          <LicenseRequestHistory enabled />
        </div>
      ) : null}
    </section>
  );
}
